<template>
  <v-dialog :value="value" max-width="460" @input="$emit('input', $event)">
    <v-card>
      <v-card-title class="text-subtitle-1">{{ $t('org-dashboard.annual-report-dialog.title') }}</v-card-title>
      <v-card-text>
        <div class="text-caption grey--text">{{ $t('org-dashboard.annual-report-dialog.report-type') }}</div>
        <v-radio-group v-model="reportType" class="mt-1" dense hide-details>
          <v-radio v-for="type in reportTypes" :key="type.value" :value="type.value" :label="type.text" />
        </v-radio-group>

        <div class="text-caption grey--text mt-4">{{ $t('org-dashboard.annual-report-dialog.period-type') }}</div>
        <v-radio-group v-model="periodType" class="mt-1" dense hide-details>
          <v-radio value="calendar" :label="$t('org-dashboard.annual-report-dialog.period-calendar')" />
          <v-radio value="rolling" :label="$t('org-dashboard.annual-report-dialog.period-rolling')" />
        </v-radio-group>

        <div class="mt-4">
          <v-progress-linear v-if="loadingPeriods" indeterminate color="primary" />
          <!-- Chaque choix dit les mois qu'il couvre : on voit ce qu'on va obtenir. -->
          <v-select
              v-else
              v-model="year"
              :items="choices"
              item-value="year"
              item-text="text"
              :label="$t('org-dashboard.annual-report-dialog.' + (reportType === 'annual' ? 'year' : 'since-year'))"
              :hint="hint"
              :no-data-text="$t('org-dashboard.annual-report-dialog.no-choice')"
              persistent-hint
              outlined
              dense
          />
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn small text @click="$emit('input', false)">{{ $t('cancel') }}</v-btn>
        <v-btn small color="primary" depressed :loading="downloading" :disabled="!year" @click="download">
          <v-icon left small>mdi-file-pdf-box</v-icon>
          {{ $t('org-dashboard.annual-report-dialog.download') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Vue from 'vue'

/**
 * Chaque bilan a sa route (et, pour la synthèse, sa version) ; tous prennent la même période. La synthèse
 * du suivi d'exploitation propose les mêmes périodes que le suivi complet.
 */
const REPORTS = {
  annual: { url: 'annual-report-v2', periods: 'annual' },
  operations: { url: 'operations-report', periods: 'operations' },
  summary: { url: 'operations-report', periods: 'operations', version: 'summary' },
}

/**
 * Le bilan annuel ou le suivi d'exploitation. On choisit des années civiles (janvier à décembre) ou
 * glissantes (jusqu'au dernier mois complet), puis une année : le bilan annuel couvre cette année-là, le
 * suivi d'exploitation en part et va jusqu'à aujourd'hui. Les choix viennent du serveur, avec les mois
 * qu'ils couvrent.
 */
export default {
  name: 'AnnualReportDialog',

  props: {
    value: { type: Boolean, default: false },
    organizationId: { type: String, required: true },
    scope: { type: String, default: 'tree' },
  },

  data() {
    return {
      reportType: 'annual',
      periodType: 'calendar',
      year: null,
      periodChoices: null,
      loadingPeriods: false,
      downloading: false,
    }
  },

  computed: {
    reportTypes() {
      return [
        { value: 'annual', text: this.$t('org-dashboard.annual-report-dialog.type-annual') },
        { value: 'operations', text: this.$t('org-dashboard.annual-report-dialog.type-operations') },
        { value: 'summary', text: this.$t('org-dashboard.annual-report-dialog.type-summary') },
      ]
    },

    /** « 2025 », ou « 2024 · sept. 2024 – août 2026 » : l'année et les mois qu'elle donne. */
    choices() {
      const choices = this.periodChoices?.[REPORTS[this.reportType].periods]?.[this.periodType] || []
      return choices.map(choice => ({
        year: choice.year,
        text: String(choice.year) === choice.label
          ? choice.label
          : this.$t('org-dashboard.annual-report-dialog.choice', { year: choice.year, months: choice.label }),
      }))
    },

    hint() {
      return this.$t('org-dashboard.annual-report-dialog.hint-' + REPORTS[this.reportType].periods + '-' + this.periodType)
    },
  },

  watch: {
    value(open) {
      if (open) {
        this.loadPeriods()
      }
    },

    reportType() {
      this.preselect()
    },

    periodType() {
      this.preselect()
    },
  },

  methods: {
    loadPeriods() {
      this.loadingPeriods = true
      Vue.auth.fetch({
        url: '/api/organization/' + this.organizationId + '/annual-report-v2/years',
        method: 'get',
        // En GET, auth.fetch transmet `data` en paramètres d'URL.
        data: { scope: this.scope || 'tree' },
      }).then(res => {
        this.periodChoices = res.data.choices || null
        this.preselect()
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.loadingPeriods = false
      })
    },

    /** L'année dernière si elle est proposée (la dernière année complète), sinon le choix le plus récent. */
    preselect() {
      const lastYear = new Date().getFullYear() - 1
      const years = this.choices.map(choice => choice.year)
      this.year = years.includes(lastYear) ? lastYear : (years[0] ?? null)
    },

    download() {
      const report = REPORTS[this.reportType]
      const data = { period: this.periodType, year: this.year, scope: this.scope || 'tree' }
      if (report.version) data.version = report.version
      this.downloading = true
      Vue.auth.download({
        url: '/api/organization/' + this.organizationId + '/' + report.url,
        method: 'get',
        // En GET, auth.fetch transmet `data` en paramètres d'URL ; `params` serait ignoré.
        data,
      }).then(() => {
        this.$emit('input', false)
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.downloading = false
      })
    },
  },
}
</script>
